import { cn } from '@/lib/classNames';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import {
	type ComponentPropsWithoutRef,
	type ElementRef,
	forwardRef
} from 'react';

const TabsRoot = TabsPrimitive.Root;

type TabsListProps = ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
	wrap?: boolean;
	marginLess?: boolean;
};
const TabsList = forwardRef<
	ElementRef<typeof TabsPrimitive.List>,
	TabsListProps
>(({ wrap = false, className, marginLess = false, ...props }, ref) => (
	<div
		data-is-wrap={wrap}
		data-is-margin-less={marginLess}
		className={cn(
			'flex w-full shrink-0',
			'data-[is-margin-less=false]:mb-4',
			'data-[is-wrap=false]:overflow-hidden data-[is-wrap=false]:overflow-x-auto'
		)}
	>
		<TabsPrimitive.List
			ref={ref}
			data-is-wrap={wrap}
			className={cn(
				'inline-flex w-full items-end',
				'data-[is-wrap=true]:flex-wrap data-[is-wrap=true]:justify-center data-[is-wrap=true]:gap-2',
				'data-[is-wrap=false]:border-b data-[is-wrap=false]:border-dm-black3',
				className
			)}
			{...props}
		/>
	</div>
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = forwardRef<
	ElementRef<typeof TabsPrimitive.Trigger>,
	ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
	<TabsPrimitive.Trigger
		ref={ref}
		className={cn(
			'relative inline-flex w-full items-center justify-center whitespace-nowrap rounded-t-md px-4 py-1.5 text-sm font-medium uppercase transition-all',
			'before:absolute before:bottom-0 before:left-0 before:right-0 before:h-0.5 before:w-full before:translate-y-full before:bg-transparent before:content-[""]',
			'disabled:cursor-not-allowed disabled:opacity-50',
			'data-[state=inactive]:hover:text-dm-gray',
			'data-[state=active]:border-dm-white data-[state=active]:text-dm-white data-[state=active]:before:bg-orange',
			className
		)}
		{...props}
	/>
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = forwardRef<
	ElementRef<typeof TabsPrimitive.Content>,
	ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
	<TabsPrimitive.Content
		ref={ref}
		className={cn('flex-initial data-[state=active]:flex-1', className)}
		{...props}
	/>
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { TabsRoot, TabsList, TabsTrigger, TabsContent };
